import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../provider/useAuth";
import { useRecaptcha } from "../../hooks/useRecaptcha";
import AuthInput from "./AuthInput";
import { passwordRules, usernameRules } from "../../utils/validationRules";
import "./Login.css";

export type SharedLoginFormInputs = {
  username: string;
  password: string;
};

// onSubmit へ渡す値。reCAPTCHA v3 トークンを付与する（§4.1）。
export type SharedLoginSubmitValues = SharedLoginFormInputs & {
  recaptchaToken?: string;
};

export type SharedLoginPageProps = {
  logoSrc?: string;
  logoAlt?: string;
  logoStyle?: React.CSSProperties;
  renderLogo?: React.ReactNode;
  loading?: boolean;
  onSubmit?: (
    values: SharedLoginSubmitValues,
  ) => Promise<{ ok: boolean; message?: string }>;
  forgotPasswordTo?: string;
  forgotPasswordText?: string;
  buttonLabel?: string;
  defaultRedirectPath?: string;
  /**
   * ログイン成功時の戻り先 URL を返す (例: ログアウト/失効前にいた画面)。
   * 値があれば defaultRedirectPath より優先。呼ぶと消費する想定 (1 度だけ)。
   */
  getRedirectPath?: () => string | null | undefined;
  /** 保存済みURLへ復帰する場合に付与する React Router の location.state。 */
  getRedirectState?: (redirectPath: string) => unknown;
};

const LoginPage: React.FC<SharedLoginPageProps> = ({
  logoSrc,
  logoAlt,
  logoStyle,
  renderLogo,
  loading,
  onSubmit,
  forgotPasswordTo = "/checkmail",
  forgotPasswordText = "パスワードをお忘れですか？",
  buttonLabel = "ログイン",
  defaultRedirectPath = "/dashboard",
  getRedirectPath,
  getRedirectState,
}) => {
  const [loginStatus, setLoginStatus] = useState("");
  const [apiError, setApiError] = useState("");
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { login } = useAuth();
  // reCAPTCHA v3。ログイン画面表示時にスクリプトをロードし、送信時にトークンを取得。
  const { executeRecaptcha } = useRecaptcha();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SharedLoginFormInputs>();

  const usernameValue = watch("username");
  const passwordValue = watch("password");
  const isFormValid = usernameValue?.trim() && passwordValue?.trim();

  const doSubmit: SubmitHandler<SharedLoginFormInputs> = async (data) => {
    setApiError("");
    try {
      if (onSubmit) {
        // 送信直前に reCAPTCHA v3 トークンを取得（action: "login"）。
        // サイトキー未設定時は undefined（BE 側で検証スキップ）。
        const recaptchaToken = await executeRecaptcha("login");
        const result = await onSubmit({ ...data, recaptchaToken });
        if (!result.ok) {
          setLoginStatus("fail");
          setApiError(result.message || "ログインに失敗しました");
          return;
        }
      } else {
        // default mock: succeed
        login({ id: "1", name: data.username });
      }

      setLoginStatus("success");
      const stateRedirectPath = location?.state?.from?.pathname;
      const storedRedirectPath = stateRedirectPath
        ? undefined
        : getRedirectPath?.();
      const fromPath =
        stateRedirectPath || storedRedirectPath || defaultRedirectPath;
      navigate(fromPath, {
        replace: true,
        state:
          storedRedirectPath && getRedirectState
            ? getRedirectState(storedRedirectPath)
            : undefined,
      });
    } catch (_e) {
      setLoginStatus("fail");
      setApiError("ログインに失敗しました");
    }
  };

  return (
    <Box className="container">
      {loading === false && loginStatus === "success" && (
        // keep placeholder for possible loading overlay
        <></>
      )}
      {loginStatus !== "success" && (
        <Box
          component="form"
          onSubmit={handleSubmit(doSubmit)}
          noValidate
          autoComplete="off"
          className="formContainer"
        >
          <Box className="fieldContainer">
            <Box className="logoContainer">
              {renderLogo ? (
                renderLogo
              ) : logoSrc ? (
                <img src={logoSrc} alt={logoAlt || "Logo"} style={logoStyle} />
              ) : null}
            </Box>
            <Box className="inputContainer">
              <AuthInput
                label="メールアドレス"
                name="username"
                register={register}
                error={!!errors.username}
                helperText={
                  typeof errors.username?.message === "string"
                    ? errors.username?.message
                    : undefined
                }
                type="email"
                rules={usernameRules}
              />
              <AuthInput
                label="パスワード"
                name="password"
                type="password"
                register={register}
                error={!!errors.password}
                helperText={
                  typeof errors.password?.message === "string"
                    ? errors.password?.message
                    : undefined
                }
                apiError={apiError}
                rules={passwordRules}
                showPasswordToggle={true}
              />
            </Box>
            <Button
              type="submit"
              disableElevation
              variant="contained"
              onMouseDown={(_e) => _e.preventDefault()}
              className={`button ${!isFormValid || loading ? "buttonDisable" : ""}`}
              disabled={!isFormValid || !!loading}
              sx={{ "&:disabled": { backgroundColor: "#BFE6EF !important" } }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                buttonLabel
              )}
            </Button>
            {/* forgotPasswordTo が絶対URL(http/https)なら外部アプリ(設定画面FE等)へ遷移するため
                react-router の Link ではなく素の <a> を使う。相対パスは従来どおり内部遷移。 */}
            {/^https?:\/\//.test(forgotPasswordTo) ? (
              <a href={forgotPasswordTo} className="link">
                {forgotPasswordText}
              </a>
            ) : (
              <Link to={forgotPasswordTo} className="link">
                {forgotPasswordText}
              </Link>
            )}
          </Box>
        </Box>
      )}
      {/* フッター（画面下部固定）。reCAPTCHA 帰属表示（左下・Google 規約）＋
          コピーライト（中央）。バッジ非表示のため帰属文言は必須。 */}
      <footer className="loginFooter">
        <span className="recaptchaFooter">
          このサイトはreCAPTCHAによって保護されています
          <br />
          ©2026 StartGear
        </span>
      </footer>
    </Box>
  );
};

export default LoginPage;
